import KoobPlaceholder from './KoobPlaceholder';
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { webUrl } from '@koob/utils';
import KoobNavbarAvatar from './KoobNavbarAvatar';
import KoobBookingsChart, { KoobBookingsMonth } from './KoobBookingsChart';

type Contact = {
  firstName?: string;
  lastName?: string;
  job?: string;
  email?: string;
  phoneNumber?: string;
};

type OrganizationRef = { id: string | number; name: string };

export type KoobOrganizationOverviewData = {
  avatar?: { imageUrl?: string } | null;
  website?: string;
  phoneNumber?: string;
  bookingReceptionEmail?: string;
  adress?: string;
  zipCode?: string;
  city?: { title?: string; country?: { title?: string } } | null;
  language?: string;
  currency?: string;
  countriesCount?: number;
  regionsCount?: number;
  markets?: { id: string | number; name: string }[];
  mainContact?: Contact | null;
  technicalContact?: Contact | null;
  scope?: string;
  isGroup?: boolean;
  parent?: OrganizationRef | null;
  children?: OrganizationRef[];
};

/** GET /api/internal/organizations/:id/overview */
export type KoobOrganizationActivity = {
  bookings: KoobBookingsMonth[];
  bookingsThisYear: number;
  cancellationRate: number | null;
  refusalRate: number | null;
  lastBookingAt: string | null;
  lastSignInAt: string | null;
  connections: { confirmed: number; pending: number };
};

type KoobOrganizationOverviewProps = {
  organization: KoobOrganizationOverviewData;
  /** `undefined` while it loads, `null` when it failed to. */
  activity?: KoobOrganizationActivity | null;
  /** `undefined` while the users load. */
  usersCount?: number;
  /** Where a related organization's page lives (parent, group members). */
  organizationPath?: (id: string | number) => string;
  /** The connections page, when the viewer may open it. */
  connectionsPath?: string;
  /** The latest audit-log events, when the viewer may read them. */
  recentEvents?: React.ReactNode;
};

const rate = (value?: number | null) => (value == null ? '–' : `${value}%`);

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : undefined;

const languageName = (code?: string) => {
  if (!code) return undefined;
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
  } catch {
    return code;
  }
};

const fullName = (contact: Contact) =>
  [contact.firstName, contact.lastName].filter(Boolean).join(' ');

function Card({
  title,
  aside,
  children,
}: {
  title: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0 rounded-2xl border border-solid border-k-gray bg-white">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-0 border-b border-solid border-k-gray px-6 py-4">
        <h2 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-k-p">
          {title}
        </h2>
        {aside && <div className="text-xs text-k-p">{aside}</div>}
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  /** `undefined` while loading. */
  value?: React.ReactNode;
  hint?: React.ReactNode;
  to?: string;
}) {
  const body = (
    <>
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-k-p">
        {label}
      </div>
      <div className="mt-2 text-[28px] font-bold leading-9 tabular-nums text-k-h">
        {value ?? (
          <KoobPlaceholder className="inline-block h-7 w-10 rounded align-middle" />
        )}
      </div>
      {hint && <div className="mt-0.5 text-xs text-k-p">{hint}</div>}
    </>
  );
  const className =
    'block rounded-2xl border border-solid border-k-gray bg-white px-4 py-4 no-underline sm:px-5 sm:py-5';
  return to ? (
    <Link to={to} className={`${className} hover:border-navy-300`}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 py-2">
      <i className={`${icon} fa-fw mt-0.5 text-navy-300`} aria-hidden />
      {/* Phones: the label above its value. */}
      <div className="flex min-w-0 flex-col sm:flex-row sm:gap-3">
        <dt className="shrink-0 text-sm text-k-p sm:w-40">{label}</dt>
        <dd className="m-0 min-w-0 text-sm font-medium text-k-h [overflow-wrap:anywhere]">
          {children}
        </dd>
      </div>
    </div>
  );
}

function Person({ role, contact }: { role: string; contact: Contact }) {
  return (
    <div className="flex gap-3">
      <KoobNavbarAvatar name={fullName(contact)} />
      <div className="min-w-0 text-sm [overflow-wrap:anywhere]">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-k-p">
          {role}
        </div>
        <div className="font-semibold text-k-h">{fullName(contact)}</div>
        {contact.job && <div className="text-k-p">{contact.job}</div>}
        <div className="mt-1 flex flex-col gap-0.5">
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="truncate">
              {contact.email}
            </a>
          )}
          {contact.phoneNumber && (
            <a href={`tel:${contact.phoneNumber}`}>{contact.phoneNumber}</a>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * An organization's Overview tab, in both apps: its bookings and activity,
 * how to reach it, its contacts and its group. A group's figures add up its
 * members'.
 */
export default function KoobOrganizationOverview({
  organization,
  activity,
  usersCount,
  organizationPath = (id) => `/organizations/${id}`,
  connectionsPath,
  recentEvents,
}: KoobOrganizationOverviewProps) {
  const { t } = useTranslation('organization');
  const address = [
    organization.adress,
    [organization.zipCode, organization.city?.title].filter(Boolean).join(' '),
    organization.city?.country?.title,
  ]
    .filter(Boolean)
    .join(', ');
  const contacts = [
    [t('detail.mainContact'), organization.mainContact],
    [t('detail.technicalContact'), organization.technicalContact],
  ].filter(([, contact]) => contact) as [string, Contact][];
  const members = organization.children ?? [];
  const failed = activity === null;
  const coverage = !organization.isGroup && [
    t('detail.country', { count: organization.countriesCount ?? 0 }),
    t('detail.region', { count: organization.regionsCount ?? 0 }),
    t('overview.marketsCount', { count: organization.markets?.length ?? 0 }),
  ];

  return (
    <div className="flex w-full flex-col gap-6">
      {organization.isGroup && (
        <Card title={t('overview.members', { count: members.length })}>
          {members.length ? (
            <ul className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {members.map((member) => (
                <li key={member.id}>
                  <Link
                    to={organizationPath(member.id)}
                    className="flex items-center gap-2 rounded-[10px] border border-solid border-k-gray px-3 py-2.5 text-sm font-medium text-k-h no-underline hover:border-navy-300 hover:bg-navy-50"
                  >
                    <i className="fa-light fa-building text-navy-300" />
                    <span className="truncate">{member.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-sm text-k-p">{t('overview.noMembers')}</p>
          )}
        </Card>
      )}

      <div
        className={`grid grid-cols-2 gap-4 ${
          organization.isGroup
            ? 'lg:grid-cols-4'
            : 'sm:grid-cols-3 xl:grid-cols-5'
        }`}
      >
        {/* A group's own users are usually none: no tile for them. */}
        {!organization.isGroup && (
          <Stat label={t('overview.users')} value={usersCount} />
        )}
        <Stat
          label={t('overview.bookingsThisYear')}
          value={failed ? '–' : activity?.bookingsThisYear}
          hint={
            activity &&
            (activity.lastBookingAt
              ? t('overview.lastBooking', {
                  date: formatDate(activity.lastBookingAt),
                })
              : t('overview.noBooking'))
          }
        />
        <Stat
          label={t('overview.cancellationRate')}
          value={failed ? '–' : activity && rate(activity.cancellationRate)}
          hint={activity && t('overview.cancellationRateHint')}
        />
        <Stat
          label={t('overview.refusalRate')}
          value={failed ? '–' : activity && rate(activity.refusalRate)}
          hint={activity && t('overview.refusalRateHint')}
        />
        <Stat
          label={t(
            organization.scope === 'to'
              ? 'overview.connectedDmcs'
              : 'overview.connectedTos',
          )}
          value={failed ? '–' : activity?.connections.confirmed}
          hint={
            activity?.connections.pending
              ? t('overview.pendingRequests', {
                  count: activity.connections.pending,
                })
              : undefined
          }
          to={connectionsPath}
        />
      </div>

      <Card
        title={t('overview.bookingsByMonth')}
        aside={t('overview.bookingsByMonthHint')}
      >
        {failed ? (
          <p className="m-0 text-sm text-k-p">{t('overview.loadError')}</p>
        ) : activity ? (
          <KoobBookingsChart months={activity.bookings} />
        ) : (
          <KoobPlaceholder className="h-48 rounded" />
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={t('overview.contactDetails')}>
          <dl className="m-0 divide-y divide-[#F0F2F5]">
            {organization.website && (
              <Row icon="fa-regular fa-globe" label={t('detail.website')}>
                {webUrl(organization.website) ? (
                  <a
                    href={webUrl(organization.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {organization.website.replace(/^https?:\/\//, '')}
                  </a>
                ) : (
                  organization.website
                )}
              </Row>
            )}
            {organization.bookingReceptionEmail && (
              <Row
                icon="fa-regular fa-envelope"
                label={t('detail.bookingReceptionEmail')}
              >
                <a href={`mailto:${organization.bookingReceptionEmail}`}>
                  {organization.bookingReceptionEmail}
                </a>
              </Row>
            )}
            {organization.phoneNumber && (
              <Row icon="fa-regular fa-phone" label={t('overview.phone')}>
                <a href={`tel:${organization.phoneNumber}`}>
                  {organization.phoneNumber}
                </a>
              </Row>
            )}
            {address && (
              <Row
                icon="fa-regular fa-location-dot"
                label={t('overview.address')}
              >
                {address}
              </Row>
            )}
            {organization.language && (
              <Row icon="fa-regular fa-language" label={t('overview.language')}>
                {languageName(organization.language)}
              </Row>
            )}
            {organization.currency && (
              <Row icon="fa-regular fa-coins" label={t('overview.currency')}>
                {organization.currency}
              </Row>
            )}
            {coverage && (
              <Row
                icon="fa-regular fa-earth-europe"
                label={t('overview.coverage')}
              >
                {coverage.join(' · ')}
              </Row>
            )}
          </dl>
        </Card>

        <Card title={t('overview.contacts')}>
          {contacts.length ? (
            <div className="flex flex-col gap-5">
              {contacts.map(([role, contact]) => (
                <Person key={role} role={role} contact={contact} />
              ))}
            </div>
          ) : (
            <p className="m-0 text-sm text-k-p">{t('overview.noContact')}</p>
          )}
        </Card>
      </div>

      <Card
        title={t('overview.activity')}
        aside={
          activity &&
          (activity.lastSignInAt
            ? t('overview.lastSignIn', {
                date: formatDate(activity.lastSignInAt),
              })
            : t('overview.neverSignedIn'))
        }
      >
        {recentEvents ?? (
          <p className="m-0 text-sm text-k-p">{t('overview.noActivity')}</p>
        )}
      </Card>

      {organization.parent && (
        <Card title={t('overview.group')}>
          <p className="m-0 text-sm text-k-p">
            {t('overview.partOf')}{' '}
            <Link to={organizationPath(organization.parent.id)}>
              {organization.parent.name}
            </Link>
          </p>
        </Card>
      )}
    </div>
  );
}
